/**
 * Login.jsx — AOS-BRAND-SYNC-01 v2
 * Brand-synchronized authentication entry point.
 *
 * BRAND GOVERNANCE:
 *   - Messaging sourced from /src/lib/brand.js (canonical; verified vs academiaos.ai 2026-11-01)
 *   - No internal engineering statistics (roles/modules/phases) in public-facing copy.
 *   - No unverified SSO claims.
 *   - No pre-filled credentials in form default state.
 *   - Demo account buttons preserved for authorized demo environment.
 *   - Authentication logic (email/password + Emergent Google SSO) unchanged.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { BRAND } from "../lib/brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";

// ── Demo accounts (authorized demo environment only) ─────────────────────────
// Passwords are present here for the demo auto-fill flow.
// GOVERNANCE: Ensure this file is never shipped to a production tenant
// without review.  Refer brand.js discrepancy item #1.
const DEMO = [
  { label: "Super Admin",     email: "admin@academiaos.ai",            password: "Sup3r@AcadOS!2026" },
  { label: "VCE · Principal", email: "principal@vaagdevi.edu.in",      password: "D3mo@AcadOS!2026" },
  { label: "ISB · Dean",      email: "shankar.dean@isb.edu",           password: "D3mo@AcadOS!2026" },
  { label: "EAIC · Executive",email: "khalid.exec@eaic.gov.ae",        password: "D3mo@AcadOS!2026" },
];

// Portal-verified background image
const BG =
  "https://static.prod-images.emergentagent.com/jobs/3df0a28a-599b-41d8-ba79-c6c0851c9972/images/98907276cec4c68a2a3b20264a553bb8a85d020955b70f60875664c47420cdbc.png";

// Brand purple (portal accent — "together." color)
const BRAND_PURPLE = "#5B2BE2";

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  // AOS-BRAND-SYNC-01: Form starts empty — no default credential pre-fill.
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [busy,     setBusy]     = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      const httpStatus = err?.response?.status;
      const detail     = err?.response?.data?.detail;
      if (!err?.response) {
        setError("Cannot reach the server. Please check your connection and try again.");
      } else if (httpStatus === 401) {
        setError("Invalid email or password.");
      } else if (httpStatus === 403) {
        setError(detail || "Account inactive. Contact your administrator.");
      } else if (httpStatus === 422) {
        setError(formatApiError(detail) || "Please check your email and password.");
      } else {
        setError(formatApiError(detail) || err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5" data-testid="login-page">

      {/* ── Left panel: brand / visual ───────────────────────────────────── */}
      <div
        className="relative hidden lg:flex lg:col-span-3 flex-col justify-between p-12 text-white overflow-hidden"
        data-testid="login-brand-panel"
      >
        {/* Background */}
        <img
          src={BG}
          alt="AcademiaOS — university campus"
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[hsl(222.2,47.4%,11.2%)]/80" />

        {/* Logo */}
        <div className="relative flex items-center gap-3" data-testid="login-brand-logo">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-[hsl(222.2,47.4%,11.2%)] text-xl font-bold select-none">
            A
          </div>
          <div>
            <div className="font-semibold tracking-tight text-lg" data-testid="login-brand-name">
              {BRAND.productRendered}
            </div>
            <div
              className="text-[11px] uppercase tracking-[0.22em] text-white/60"
              data-testid="login-brand-category"
            >
              {BRAND.category}
            </div>
          </div>
        </div>

        {/* Hero copy */}
        <div className="relative max-w-xl" data-testid="login-hero-copy">
          <div className="label-eyebrow text-white/70 mb-4 text-xs tracking-[0.2em]">
            {BRAND.eyebrows.hero}
          </div>

          {/* Headline — "together." accented in brand purple */}
          <h1
            className="text-4xl sm:text-5xl font-semibold leading-tight tracking-tight"
            style={{ fontFamily: "var(--font-heading)" }}
            data-testid="login-hero-headline"
          >
            {/* Split "together." out to apply accent colour */}
            Education runs better{" "}
            <span style={{ color: BRAND_PURPLE }} data-testid="login-headline-accent">
              together.
            </span>
          </h1>

          <p
            className="mt-5 text-white/75 text-sm leading-relaxed max-w-md"
            data-testid="login-hero-supporting"
          >
            {BRAND.statements.supporting}
          </p>
        </div>

        {/* Trust pillars — replaces removed engineering-metric stats */}
        <div
          className="relative grid grid-cols-3 gap-3"
          data-testid="login-brand-pillars"
        >
          {BRAND.pillars.map((p) => (
            <div
              key={p.label}
              className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm"
              data-testid={`login-pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-white/50 shrink-0" />
                <span className="text-xs font-semibold text-white/90 leading-tight">
                  {p.label}
                </span>
              </div>
              <p className="text-[11px] text-white/50 leading-snug">{p.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel: authentication form ────────────────────────────── */}
      <div
        className="lg:col-span-2 flex items-center justify-center p-8 bg-card"
        data-testid="login-form-panel"
      >
        <div className="w-full max-w-sm">

          {/* Mobile logo (shown only below lg) */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-foreground text-background text-base font-bold">
              A
            </div>
            <span className="font-semibold tracking-tight">{BRAND.productRendered}</span>
          </div>

          <h2
            className="text-2xl font-semibold tracking-tight"
            data-testid="login-form-heading"
          >
            Welcome to {BRAND.product}
          </h2>
          <p
            className="mt-1 text-sm text-muted-foreground"
            data-testid="login-form-subheading"
          >
            {BRAND.category}
          </p>

          <form
            onSubmit={submit}
            className="mt-8 space-y-4"
            data-testid="login-form"
          >
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs">Work email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@institution.edu"
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
                role="alert"
                aria-live="polite"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full"
              disabled={busy}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[11px] uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Google SSO — Emergent-managed integration, unchanged */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            data-testid="login-google-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
          >
            <svg className="me-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.8 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 2.9l5.7-5.7C34 6.2 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16 18.9 13 24 13c3 0 5.8 1.1 7.9 2.9l5.7-5.7C34 7.2 29.3 5 24 5 16.3 5 9.7 9.2 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 43c5.3 0 10-2 13.6-5.3l-6.3-5.2C29.2 33.9 26.7 35 24 35c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 38.6 16.3 43 24 43z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.3 5.2C40.9 35 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Demo accounts — authorized demo environment */}
          <div className="mt-8" data-testid="demo-accounts-section">
            <div className="label-eyebrow mb-2">Demo accounts</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  data-testid={`demo-${d.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => {
                    setEmail(d.email);
                    setPassword(d.password);
                  }}
                  className="rounded-md border border-border bg-background p-2 text-start text-xs hover:bg-muted/60 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="font-medium">{d.label}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{d.email}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Legal footer */}
          <p className="mt-8 text-[10px] text-muted-foreground text-center leading-relaxed">
            {BRAND.legal.copyright} {BRAND.statements.trust}
          </p>
        </div>
      </div>
    </div>
  );
}
